import React, { useMemo, useState, type ChangeEvent } from "react";

import { $enum } from "ts-enum-util";

import {
  NotificationMethod,
  NotificationType,
  Periodicity,
  UserRole,
  type PutUserNotificationRequestBody,
  type User,
} from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { cn, Page, PhoneInput, Select, TableV2 as Table, TextField, Typography } from "@eisox/design-system";
import { BinIcon, CheckedIcon, ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon, RejectIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { userApi } from "~/api";
import {
  AddUserDrawer,
  DeleteUserDialog,
  filterUsers,
  HelpPopup,
  Role,
  RoleButton,
  UpdateUserDialog,
  type Filters,
} from "~/features/users";
import { requirePagePermission } from "~/helpers/auth/route-permission.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { camelToSnake } from "~/utils/string.utils";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/users")({
  beforeLoad: ({ context: { permissions }, params: { buildingId }, preload }) => {
    requirePagePermission(permissions, "users", buildingId, preload);
  },
  loader: async ({ context: { queryClient }, params: { buildingId } }) => {
    await queryClient.ensureQueryData(userApi.queries.building(buildingId));
  },
  head: ({ match: { context }, params: { buildingId } }) =>
    buildingPageHead(context.queryClient, buildingId, m.users()),
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { buildingId } = Route.useParams();
  const { data: users } = useSuspenseQuery(userApi.queries.building(buildingId));

  const { building, boilerRooms, role } = useBuildingContext(Route.id);

  const { mutate: updateUserNotification } = useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: PutUserNotificationRequestBody }) =>
      userApi.updateNotification(buildingId, userId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries(userApi.queries.building(buildingId));
    },
  });

  const [filters, setFilters] = useState<Filters>({ roles: [], search: "" });
  const [userToDeleteId, setUserToDeleteId] = useState<string>();

  const filteredUsers = useMemo(() => filterUsers(users, filters), [users, filters]);
  const userToDelete = filteredUsers.find(u => u.id === userToDeleteId);

  const roleOptions = $enum(UserRole)
    .getValues()
    .filter(r => r !== UserRole.MANAGER_BOILER_ROOM)
    .map(r => ({ value: r, name: m[camelToSnake(r)]() }));

  const maintenanceEmailOptions = [
    { value: "disabled", name: m.activation({ activation: "false" }) },
    { value: Periodicity.MONTHLY, name: m.monthly() },
    { value: Periodicity.WEEKLY, name: m.weekly() },
  ];

  const columnHelper = createColumnHelper<User>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("firstName", {
        header: m.first_name(),
        cell: ({ getValue }) => <Typography>{getValue() ?? "-"}</Typography>,
      }),
      columnHelper.accessor("lastName", {
        header: m.last_name(),
        cell: ({ getValue }) => <Typography>{getValue() ?? "-"}</Typography>,
      }),
      columnHelper.accessor("email", {
        header: m.email(),
        cell: ({ getValue }) => <Typography>{getValue()?.email ?? "-"}</Typography>,
      }),
      columnHelper.accessor("phoneNumber", {
        header: m.phone(),
        cell: ({ getValue }) => {
          const phone = getValue();
          const { number } = PhoneInput.parsePhoneNumber(phone?.number ?? "") ?? {};
          return (
            <div className="gap-2.5 [&_svg]:h-3.75 [&_svg]:w-3.75 flex items-center text-nowrap whitespace-nowrap [&_svg]:shrink-0">
              <Typography>{number ? PhoneInput.formatPhoneNumber(number) : "-"}</Typography>
              {phone?.verified !== undefined &&
                (phone.verified ? (
                  <CheckedIcon color={colors.primary.green} width={20} height={20} />
                ) : (
                  <RejectIcon color={colors.primary.red} width={20} height={20} />
                ))}
            </div>
          );
        },
      }),
      columnHelper.accessor("addedToBuildingAt", {
        header: m.addition_date(),
        cell: ({ getValue }) => {
          const date = getValue();
          return <Typography>{date ? dayjs(date).format("L") : "-"}</Typography>;
        },
      }),
      columnHelper.accessor("notifications", {
        header: m.maintenance_email(),
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue<User["notifications"]>();
          if (!value) return null;
          const maintenanceEmail = value.find(v => v.type === NotificationType.MAINTENANCE);
          const userId = row.original.id;
          return (
            <Select.Root
              multiple={false}
              value={maintenanceEmail?.periodicity ?? "disabled"}
              onValueChange={value => handleChangeMaintenanceEmail(value, userId)}
            >
              <Select.Trigger style={{ width: "100%" }} disabled={!row.original.email?.verified}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                <Select.Viewport>
                  {maintenanceEmailOptions.map(o => (
                    <Select.Item key={o.value} value={o.value}>
                      {o.name}
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Root>
          );
        },
      }),
      columnHelper.accessor("notifications", {
        header: m.alert(),
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue<User["notifications"]>();
          if (!value) return null;
          const alerts = value.find(v => v.type === NotificationType.GTB);
          const email = row.getValue<User["email"]>("email");
          const phone = row.getValue<User["phoneNumber"]>("phoneNumber");
          const userId = row.original.id;
          return (
            <Select.Root multiple value={alerts?.methods} onValueChange={value => handleChangeAlert(value, userId)}>
              <Select.Trigger style={{ width: "100%" }} disabled={!email?.verified && !phone?.verified}>
                <Select.Value placeholder={m.select()} />
              </Select.Trigger>
              <Select.Content>
                <Select.Viewport>
                  <Select.Item value={NotificationMethod.SMS} disabled={!phone?.verified}>
                    {m.sms()}
                  </Select.Item>
                  <Select.Item value={NotificationMethod.EMAIL} disabled={!email?.verified}>
                    {m.email()}
                  </Select.Item>
                </Select.Viewport>
              </Select.Content>
            </Select.Root>
          );
        },
      }),
      columnHelper.accessor("role", {
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const userRole = row.original.role;
          return userRole === UserRole.OWNER || row.original.addedByGroup ? (
            <RoleButton className="w-32.5 cursor-default justify-center" role={userRole}>
              {m[camelToSnake(userRole)]()}
            </RoleButton>
          ) : (
            <UpdateUserDialog
              building={building}
              user={row.original}
              boilerRoom={!!boilerRooms.length}
              userRole={role}
              disabled={!row.original.email || row.original.role === UserRole.API}
            />
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const userId = row.original.id;
          const addedByGroup = row.original.addedByGroup;
          return row.original.email && !addedByGroup ? (
            <BinIcon className="h-5 w-5 cursor-pointer" onClick={() => handleDeleteUser(userId)} />
          ) : null;
        },
      }),
    ],
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- column defs stay stable; cells close over current values
    [],
  );

  const table = useReactTable({
    data: filteredUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleChangeRoles = (value: string[]) =>
    setFilters(prev => ({ ...prev, roles: value.map(v => v as UserRole) }));

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) =>
    setFilters(prev => ({ ...prev, search: e.target.value }));

  const handleChangeMaintenanceEmail = (value: string | string[], userId: string) => {
    updateUserNotification({
      userId,
      body: [
        {
          type: NotificationType.MAINTENANCE,
          methods: value === "disabled" ? [] : [NotificationMethod.EMAIL],
          periodicity: value as Periodicity,
        },
      ],
    });
  };

  const handleChangeAlert = (value: string[], userId: string) => {
    updateUserNotification({
      userId,
      body: [
        {
          type: NotificationType.GTB,
          methods: [...value.map(v => v as NotificationMethod)],
        },
      ],
    });
  };

  const handleDeleteUser = (userId: string) => setUserToDeleteId(userId);

  return (
    <Page.Root>
      <Page.Header>
        <Page.Title>{m.users()}</Page.Title>
        <HelpPopup />
        <AddUserDrawer buildingId={buildingId} boilerRoom={boilerRooms.length > 0} users={users} userRole={role} />
      </Page.Header>
      <Page.Content className="gap-10 p-10 flex flex-col" data-testid="Users">
        <div className="gap-5 flex">
          <Select.Root multiple value={filters.roles} onValueChange={handleChangeRoles}>
            <Select.Trigger className="w-51.25">
              <Select.Value placeholder={m.roles()} />
            </Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {roleOptions.map(o => {
                  const roleCount = users.filter(
                    u =>
                      u.role === (o.value as UserRole) ||
                      (o.value === UserRole.MANAGER && u.role === UserRole.MANAGER_BOILER_ROOM),
                  ).length;
                  return (
                    <Select.Item
                      key={o.value}
                      className="gap-2.25 flex w-full flex-nowrap items-center"
                      value={o.value}
                    >
                      <Typography>{o.name}</Typography>
                      <Role role={o.value} size={8} />
                      <span style={{ marginLeft: "auto" }}>{roleCount < 10 ? `0${roleCount}` : roleCount}</span>
                      <Select.ItemText>{o.name}</Select.ItemText>
                    </Select.Item>
                  );
                })}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
          <TextField.Root
            className="max-w-85 flex-1"
            placeholder={m.search()}
            value={filters.search}
            onChange={handleChangeSearch}
          >
            <TextField.Slot>
              <MagnifyingGlassIcon />
            </TextField.Slot>
          </TextField.Root>
        </div>
        <Table.Container>
          <Table.Root className="table-fixed">
            {table.getHeaderGroups().map(headergroup => (
              <React.Fragment key={headergroup.id}>
                <colgroup>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <col key={index} />
                  ))}
                  <col width={170} />
                  <col width={50} />
                </colgroup>
                <Table.Header
                  key={headergroup.id}
                  className="top-0 sticky z-1 border-b-2 border-primary-darker-gray bg-primary-white"
                >
                  <Table.Row>
                    {headergroup.headers.map(header => (
                      <Table.Head key={header.id} onClick={header.column.getToggleSortingHandler()}>
                        <div
                          className={cn(
                            "pb-2.5 [&_svg]:ml-2.5 [&_svg]:h-3.25 [&_svg]:w-3.25 flex items-center [&_svg]:shrink-0 [&_svg]:text-primary-dark-gray",
                            header.column.getCanSort() && "cursor-pointer",
                          )}
                        >
                          <Typography>{flexRender(header.column.columnDef.header, header.getContext())}</Typography>
                          {{
                            asc: <ChevronUpIcon />,
                            desc: <ChevronDownIcon />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </Table.Head>
                    ))}
                  </Table.Row>
                </Table.Header>
              </React.Fragment>
            ))}
            <Table.Body>
              {table.getRowModel().rows.map(row => (
                <React.Fragment key={row.id}>
                  <Table.Row className="border-b-2 border-primary-darker-gray">
                    {row.getVisibleCells().map(cell => (
                      <Table.Cell
                        key={cell.id}
                        className="p-2.5 first:rounded-tl-[10px] first:rounded-bl-[10px] last:rounded-tr-[10px] last:rounded-br-[10px]"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                  <Table.Row className="h-2.5" />
                </React.Fragment>
              ))}
            </Table.Body>
          </Table.Root>
        </Table.Container>
      </Page.Content>
      {userToDelete && (
        <DeleteUserDialog
          user={userToDelete}
          building={building}
          open
          onOpenChange={() => setUserToDeleteId(undefined)}
        />
      )}
    </Page.Root>
  );
}
